/**
 * Composable for USD to EUR currency conversion.
 *
 * Fetches the exchange rate from the backend once per session and provides
 * reactive conversion helpers. The rate is cached in a module-level variable
 * so that multiple components/composable calls share the same data without
 * repeated API requests.
 *
 * Usage:
 * ```ts
 * const { formatEur, convertToEur, rate, loadRate } = useCurrency()
 * await loadRate()
 * const label = formatEur(0.0042) // "€0.0039"
 * ```
 */
import { ref, type Ref } from 'vue'
import axios from 'axios'
import { useApiCredentials } from '@/composables/useApiCredentials'
import useWrapper from '@/composables/useWrapper'

// Module-level cache — shared across all useCurrency() calls.
const _rate: Ref<number | null> = ref(null)
const _rateDate: Ref<string | null> = ref(null)
const _loading: Ref<boolean> = ref(false)
const _loaded: Ref<boolean> = ref(false)

export function useCurrency() {
    const { guid, axiosBearerHeader, portalBaseUrl } = useApiCredentials()
    const { rootAuth } = useWrapper()

    /**
     * Load the exchange rate from the backend.
     * Only fetches if not already loaded. Safe to call multiple times.
     */
    const loadRate = rootAuth(async (): Promise<void> => {
        if (_loaded.value || _loading.value) return
        _loading.value = true
        try {
            const { data } = await axios.get(
                `${portalBaseUrl}/${guid.value}/apps/exchange-rate`,
                axiosBearerHeader.value,
            )
            _rate.value = data.usd_to_eur
            _rateDate.value = data.rate_date
            _loaded.value = true
        } catch (e) {
            console.warn('Failed to load exchange rate, falling back to USD display', e)
        } finally {
            _loading.value = false
        }
    }, false)

    /**
     * Convert a USD amount to EUR using the cached rate.
     *
     * @param usd - The amount in US dollars.
     * @returns The amount in EUR, or the original USD amount if rate unavailable.
     */
    function convertToEur(usd: number): number {
        if (_rate.value === null) return usd
        return usd * _rate.value
    }

    /**
     * Format a USD amount as a EUR string.
     *
     * Falls back to `$X.XXXX` if the rate has not been loaded yet.
     *
     * @param usd - The amount in US dollars.
     * @param decimals - Number of decimal places (default: 4).
     * @returns Formatted string like `€0.0039` or `$0.0042`.
     */
    function formatEur(usd: number | undefined | null, decimals = 4): string {
        if (usd === undefined || usd === null) return '€0.00'
        if (_rate.value !== null) {
            return `€${(usd * _rate.value).toFixed(decimals)}`
        }
        return `$${usd.toFixed(decimals)}`
    }

    /**
     * Format a cost with both EUR and USD shown.
     *
     * @param usd - The amount in US dollars.
     * @param decimals - Number of decimal places (default: 4).
     * @returns Formatted string like `€0.0039 ($0.0042)`.
     */
    function formatEurWithUsd(usd: number | undefined | null, decimals = 4): string {
        if (usd === undefined || usd === null) return '€0.00'
        if (_rate.value !== null) {
            return `€${(usd * _rate.value).toFixed(decimals)} ($${usd.toFixed(decimals)})`
        }
        return `$${usd.toFixed(decimals)}`
    }

    /**
     * Format an amount that is **already stored in EUR** (no conversion).
     *
     * Use this for values coming from the database which have been converted
     * to EUR at write-time. For raw USD values (e.g. real-time streaming from
     * KMLib) use {@link formatEur} instead.
     *
     * @param eur - The amount already in EUR.
     * @param decimals - Number of decimal places (default: 4).
     * @returns Formatted string like `€0.0039`.
     */
    function displayEur(eur: number | undefined | null, decimals = 4): string {
        if (eur === undefined || eur === null) return '€0.00'
        return `€${eur.toFixed(decimals)}`
    }

    return {
        /** The current USD→EUR rate (null if not loaded). */
        rate: _rate,
        /** The date the rate applies to. */
        rateDate: _rateDate,
        /** Whether the rate has been successfully loaded. */
        loaded: _loaded,
        /** Load the exchange rate (idempotent). */
        loadRate,
        /** Convert USD to EUR. */
        convertToEur,
        /** Format USD as EUR string. */
        formatEur,
        /** Format USD as EUR string with USD in parentheses. */
        formatEurWithUsd,
        /** Format a value already stored in EUR (no conversion). */
        displayEur,
    }
}
